import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, ExternalLink, ArrowLeft, Facebook } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { API } from '../App';



const FacebookGroupsPage = ({ user, onLogout, refreshUserData }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/facebook-groups`);
      // API returns array directly, not { groups: [...] }
      setGroups(Array.isArray(response.data) ? response.data : (response.data.groups || []));
    } catch (error) {
      console.error('Error fetching Facebook groups:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === 'fr' ? 'Retour' : 'Back'}
          </Button>
          
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
            <div className="p-2 md:p-3 bg-brand-100 rounded-xl w-fit">
              <Facebook className="h-6 w-6 md:h-8 md:w-8 text-brand-600" />
            </div>
            <div>
              <h1 className="text-xl md:text-4xl font-bold text-gray-900 whitespace-nowrap">
                {language === 'fr' ? 'Trouver des billets' : 'Find Tickets'}
              </h1>
              <p className="text-sm md:text-lg text-gray-600">
                {language === 'fr' 
                  ? 'Rejoignez nos communautés pour acheter et vendre des billets'
                  : 'Join our communities to buy and sell tickets'}
              </p>
            </div>
          </div>
        </div>

        {/* Submit Group CTA - At Top */}
        <div className="mb-6 md:mb-8">
          <Card className="p-4 md:p-6 bg-gradient-to-r from-brand-50 to-brand-100 border-brand-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h3 className="text-base md:text-lg font-semibold">
                  {language === 'fr' 
                    ? 'Vous gérez un groupe Facebook de billets ?'
                    : 'Do you manage a Facebook ticket group?'}
                </h3>
                <p className="text-sm text-gray-600">
                  {language === 'fr'
                    ? 'Soumettez votre groupe pour qu\'il soit affiché ici'
                    : 'Submit your group to be displayed here'}
                </p>
              </div>
              <Button
                onClick={() => navigate('/submit-group')}
                className="bg-brand-600 hover:bg-brand-700 w-full md:w-auto"
              >
                {language === 'fr' ? 'Soumettre un groupe' : 'Submit a group'}
              </Button>
            </div>
          </Card>
        </div>

        {/* Info Banner */}
        <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 md:p-6 mb-6 md:mb-8">
          <h3 className="font-semibold text-blue-900 mb-2 text-sm md:text-base">
            {language === 'fr' ? '💡 Comment ça fonctionne ?' : '💡 How does it work?'}
          </h3>
          <p className="text-blue-800 text-sm md:text-base">
            {language === 'fr'
              ? 'Ces groupes Facebook sont des communautés où vous pouvez acheter et vendre des billets. Utilisez Middleman pour sécuriser vos transactions et vous protéger contre la fraude.'
              : 'These Facebook groups are communities where you can buy and sell tickets. Use Middleman to secure your transactions and protect yourself against fraud.'}
          </p>
        </div>

        {/* Groups Grid - No Limit */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-base md:text-lg">
              {language === 'fr' 
                ? 'Aucun groupe disponible pour le moment'
                : 'No groups available at the moment'}
            </p>
          </div>
        ) : (
          <>
            <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base">
              {groups.length} {language === 'fr' ? 'groupes disponibles' : 'groups available'}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {groups.map((group) => (
                <Card key={group.id} className="overflow-hidden hover:shadow-lg transition-shadow bg-white">
                  {group.cover_image && (
                    <div className="aspect-video overflow-hidden bg-gray-100">
                      <img
                        src={group.cover_image.startsWith('/api') 
                          ? `${process.env.REACT_APP_BACKEND_URL}${group.cover_image}`
                          : group.cover_image}
                        alt={group.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  {!group.cover_image && (
                    <div className="aspect-video bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center">
                      <Facebook className="h-16 w-16 text-white/80" />
                    </div>
                  )}
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-base md:text-lg mb-2 line-clamp-2">{group.name}</h3>
                    
                    {group.description && (
                      <p className="text-gray-600 text-xs md:text-sm mb-3 line-clamp-2">{group.description}</p>
                    )}

                    <div className="flex items-center justify-between">
                      {group.member_count && (
                        <div className="flex items-center gap-1 text-xs md:text-sm text-gray-500">
                          <Users className="h-4 w-4" />
                          <span>{group.member_count} {language === 'fr' ? 'membres' : 'members'}</span>
                        </div>
                      )}
                      
                      <a
                        href={group.url || group.facebook_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded-lg font-medium text-xs md:text-sm transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {language === 'fr' ? 'Rejoindre' : 'Join'}
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

      <Footer variant="full" />
    </div>
  );
};

export default FacebookGroupsPage;
